"use client";

import { FormCurrencyInput } from "@client/components/forms/form-currency-input";
import { FormInput } from "@client/components/forms/form-input";
import { FormTextarea } from "@client/components/forms/form-textarea";
import {
  createRoundWizardProposal,
  type CreateRoundWizardProposal,
} from "@engine/round/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  generateProposalFieldId,
  generateProposalFormId,
  generateProposalId,
} from "@lib/utils/idGeneration";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
} from "@sfc/ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useForm } from "react-hook-form";

type RoundWizardProposalProps = {
  data: CreateRoundWizardProposal;
  updateData: (data: CreateRoundWizardProposal) => void;
  onNext: () => void;
  onBack: () => void;
};

export function RoundWizardProposal({
  data,
  updateData,
  onNext,
  onBack,
}: RoundWizardProposalProps) {
  const form = useForm<CreateRoundWizardProposal>({
    resolver: zodResolver(createRoundWizardProposal),
    defaultValues: data,
  });

  function handleAddCustomField() {
    form.setValue("customFields", [
      ...form.getValues("customFields"),
      { id: generateProposalFieldId(), title: "", description: "", value: "" },
    ]);
  }

  function handleSkipClick() {
    updateData({
      proposalFormId: generateProposalFormId(),
      proposalId: generateProposalId(),
      shortname: "",
      longname: "",
      requestAmount: 0,
      customFields: [],
    });
    onNext();
  }

  const onSubmit = (values: CreateRoundWizardProposal) => {
    updateData(values);
    onNext();
  };

  return (
    <Form {...form}>
      <Card>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <CardHeader>
            <CardTitle>Add your first proposal</CardTitle>
            <CardDescription>
              Proposals are funding requests that will be evaluated in this
              round.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="shortname"
              label="Title"
              placeholder="e.g., Community Garden Project"
            />

            <FormTextarea
              control={form.control}
              name="longname"
              label="Description"
              placeholder="e.g., A community garden project that will provide fresh produce to low-income families in the neighborhood."
            />

            <FormCurrencyInput
              className="md:max-w-48"
              control={form.control}
              name="requestAmount"
              label="Request Amount"
            />

            {form.watch("customFields").map((field, index) => (
              <div
                key={field.id}
                className="border rounded-md p-4 flex flex-col gap-4"
              >
                <FormInput
                  control={form.control}
                  name={`customFields.${index}.title`}
                  label={`Custom Field ${index + 1} - Title`}
                  placeholder="e.g., Contact Email"
                />
                <FormTextarea
                  control={form.control}
                  name={`customFields.${index}.description`}
                  label={`Custom Field ${index + 1} - Description`}
                  placeholder="e.g., We will use this email to contact you."
                />
                <FormInput
                  control={form.control}
                  name={`customFields.${index}.value`}
                  label={`Custom Field ${index + 1} - Answer for this proposal`}
                  placeholder="e.g., your.email@example.com"
                />
                <Button
                  className="self-start text-destructive"
                  variant="outline"
                  type="button"
                  onClick={() =>
                    form.setValue(
                      "customFields",
                      form
                        .getValues("customFields")
                        .filter(({ id }) => id !== field.id),
                    )
                  }
                >
                  <Trash2Icon className="size-4" />
                  Remove
                </Button>
              </div>
            ))}

            <Button
              className="self-start bg-background"
              variant="outline"
              type="button"
              onClick={handleAddCustomField}
            >
              <PlusIcon className="size-4" />
              Add Custom Field
            </Button>
          </CardContent>
          <CardFooter className="flex justify-between gap-3">
            <Button type="button" variant="nav" onClick={onBack}>
              <ChevronLeftIcon className="size-4" />
              <span className="sr-only sm:not-sr-only">Back</span>
            </Button>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={handleSkipClick}>
                Skip
              </Button>
              <Button type="submit">
                Continue
                <ChevronRightIcon className="size-4" />
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </Form>
  );
}
